import { GooglePlaceComponent, getFromGooglePlace } from 'utils/location/googlePlaceUtils'
import { useCallback, useMemo, useState } from 'react'

import { GoogleAPIPlace } from 'models/purchaseFlow'
import { Nullable } from 'models/helpers'
import { Path } from 'constants/router'
import { PropertyType } from 'models/property'
import constate from 'constate'
import { isAddressValid } from 'utils/validators'
import { useCreateNewProperty } from 'dataQueries'
import { useNavigate } from 'react-router'
import { usePurchaseFlowRealEstateProperty } from '../PurchaseFlow/_main/contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const [NewPropertyProvider, useNewProperty] = constate(() => {
  const { t } = useTranslation(['new_property'])
  const navigate = useNavigate()
  const { spawnErrorToast, spawnSuccessToast } = useSnackbar()
  const { userPropertyWorkspaceId } = useUserData()

  const {
    isAddressManually,
    addressManual,
    streetNumberManual,
    cityManual,
    zipCodeManual,
    countryManual,
    propertyName,
  } = usePurchaseFlowRealEstateProperty()

  const [selectedPropertyType, setSelectedPropertyType] = useState<Nullable<PropertyType>>(null)
  const [selectedPlace, setSelectedPlace] = useState<GoogleAPIPlace | null>(null)

  const createNewProperty = useCreateNewProperty()

  const isCreatePropertyDisabled = useMemo(() => {
    if (isAddressManually && (!addressManual || !cityManual || !zipCodeManual || !countryManual)) return true
    if (!isAddressManually && !isAddressValid(selectedPlace)) return true
    if (!selectedPropertyType) return true
    if (!propertyName) return true

    return false
  }, [addressManual, cityManual, countryManual, isAddressManually, propertyName, selectedPlace, selectedPropertyType, zipCodeManual])

  const selectedPlacesFormatted = useMemo(() => {
    if (!selectedPlace) return

    return {
      street: selectedPlace.formatted_address,
      streetNumber: getFromGooglePlace(selectedPlace, GooglePlaceComponent.streetNumber),
      city: getFromGooglePlace(selectedPlace, GooglePlaceComponent.city),
      postalCode: getFromGooglePlace(selectedPlace, GooglePlaceComponent.postalCode),
      country: getFromGooglePlace(selectedPlace, GooglePlaceComponent.country),
      coordinates: {
        lat: selectedPlace.geometry.location.lat,
        lng: selectedPlace.geometry.location.lng,
      }
    }
  }, [selectedPlace])

  const submitCreateNewProperty = useCallback(() => {
    if (!selectedPlacesFormatted || !userPropertyWorkspaceId || isCreatePropertyDisabled) return

    createNewProperty.mutate({
      workspaceId: userPropertyWorkspaceId,
      data: {
        title: propertyName,
        address: {
          street: isAddressManually ? addressManual : selectedPlacesFormatted.street,
          streetNumber: isAddressManually ? streetNumberManual : selectedPlacesFormatted.streetNumber,
          city: isAddressManually ? cityManual : selectedPlacesFormatted.city,
          postalCode: isAddressManually ? zipCodeManual : selectedPlacesFormatted.postalCode,
          country: isAddressManually ? countryManual : selectedPlacesFormatted.country,
          coordinates: {
            lat: selectedPlacesFormatted.coordinates.lat,
            lng: selectedPlacesFormatted.coordinates.lng,
          }
        },
        propertyUsage: selectedPropertyType!, // we are sure that selectedPropertyType is not null because of isCreatePropertyDisabled check
      }
    },
      {
        onSuccess: (data) => {
          spawnSuccessToast(t('create_property_success'), { timeout: 3000 })

          if (data.data.id) {
            navigate(Path.PROPERTY_VISUALS_GALLERY.replace(':id', data.data.id)) // we want to enforce user to order visuals after property creation
          }
        },
        onError: (error) => {
          spawnErrorToast(t('create_property_error'), { error: error.request })
        }
      }
    )
    // skip exhaustive deps: spawnSuccessToast, t, navigate, spawnErrorToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlacesFormatted, userPropertyWorkspaceId, createNewProperty, propertyName, isAddressManually, addressManual, streetNumberManual, cityManual, zipCodeManual, countryManual])

  return {
    selectedPropertyType,
    setSelectedPropertyType,
    selectedPlace,
    setSelectedPlace,
    submitCreateNewProperty,
    createNewProperty,
    isCreatePropertyDisabled,
  }
})
